import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const Hero: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 1000], ["0%", "30%"]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0]);

  return (
    <section className="relative min-h-screen w-full overflow-hidden flex flex-col justify-center px-4 pt-36 md:pt-40 lg:pt-40 pb-12">
      {/* ======================
          BG VIDEO (OPTIMIZED OBJECT-COVER & PARALLAX)
      ====================== */}
      <motion.div 
        className="absolute inset-0 z-0 overflow-hidden pointer-events-none bg-black"
        style={{ y: backgroundY, opacity }}
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        >
          <source src="/bg.webm" type="video/webm" />
        </video>

        {/* Overlays */}
        <div className="absolute inset-0 bg-black/30 z-[1]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/80 z-[1]" />
      </motion.div>

      {/* GOLD HALO SHAPES */}
      <div className="absolute inset-0 z-[1] pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute top-[-10%] right-[-10%] w-[60vw] h-[75vh] bg-[#a5694f]/18 rounded-bl-[12rem] blur-2xl md:blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bottom-[-10%] left-[-10%] w-[65vw] h-[60vh] bg-[#a5694f]/14 rounded-tr-[14rem] blur-2xl md:blur-3xl"
        />
      </div>

      {/* CONTENT */}
      <div className="relative z-10 max-w-[1400px] mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-12 relative mb-14 lg:mb-28">
            <motion.h1
              initial={{ opacity: 0, y: 80, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              style={{ willChange: "transform, opacity, filter" }}
              className="font-oswald font-bold uppercase tracking-tighter leading-[1.1] pt-4 pb-4 text-white text-[clamp(60px,11vw,180px)] drop-shadow-[0_25px_60px_rgba(0,0,0,0.55)]"
            >
              DEM
            </motion.h1>

            <motion.h1
              initial={{ opacity: 0, y: 80, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 1.1, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              style={{ willChange: "transform, opacity, filter" }}
              className="font-oswald font-bold uppercase tracking-tighter text-transparent bg-clip-text leading-[1.1] pt-4 pb-10 text-[clamp(60px,11vw,180px)] bg-gradient-to-r from-[#8A6930] via-[#a5694f] to-[#6F5327] lg:pl-[20vw] drop-shadow-[0_25px_60px_rgba(0,0,0,0.35)]"
            >
              {t("hero.brand")}
            </motion.h1>
          </div>

          <div className="lg:col-start-8 lg:col-span-5 flex flex-col gap-10">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-playfair text-xl md:text-2xl text-white/80 leading-relaxed italic border-l-2 border-[#a5694f]/60 pl-6"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col sm:flex-row gap-6 mt-4"
            >
              <button
                type="button"
                onClick={() => navigate("/properties")}
                className="group relative px-10 py-5 bg-transparent text-white font-oswald font-light text-xs uppercase tracking-[0.3em] overflow-hidden border border-white/30 hover:border-white transition-all duration-500"
              >
                <span className="relative z-10 flex items-center gap-4 transition-all">
                  {t("hero.ctaProperties")} <ArrowRight className="w-4 h-4 opacity-70 group-hover:opacity-100 group-hover:translate-x-2 transition-all duration-500" />
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate("/contact")}
                className="px-10 py-5 border border-transparent text-white font-oswald font-light text-xs uppercase tracking-[0.3em] hover:text-white/70 transition-colors duration-500 relative after:absolute after:bottom-4 after:left-10 after:right-10 after:h-[1px] after:bg-white/30 hover:after:bg-white/70 after:transition-colors"
              >
                {t("hero.ctaContact")}
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-10 left-10 hidden md:block z-10"
      >
        <span className="font-oswald text-xs uppercase tracking-[0.35em] [writing-mode:vertical-rl] rotate-180 text-white/35">
          {t("hero.signature")}
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
